import type { CustomSurveyResults, TemplateQuestionTrendDataPoint, TemplateQuestionTrends } from '../../api/customSurveys';
import { QuestionType } from '../../api/customSurveyTemplates';

export type LatestPair = {
  current: TemplateQuestionTrendDataPoint;
  previous?: TemplateQuestionTrendDataPoint;
};

export type CategoryGroup = {
  category: string | null;
  questionCount: number;
  responseCount: number;
  /** Alle spørsmålene i kategorien, i malens rekkefølge, også de uten svar */
  questions: TemplateQuestionTrends[];
  /** Skalaspørsmålene blant dem, som sammenlignes i ett kort når de er flere */
  likert: TemplateQuestionTrends[];
};

export type ChoiceRow = {
  key: string;
  label: string;
  count: number;
  percent: number;
  isTop: boolean;
};

export type NpsCategory = 'poor' | 'good' | 'veryGood' | 'excellent';

/** Siste og forrige undersøkelse for ett spørsmål, eldste først i trendData */
export const latestPair = (question: TemplateQuestionTrends): LatestPair | null => {
  const { trendData } = question;
  if (trendData.length === 0) return null;
  return { current: trendData[trendData.length - 1], previous: trendData[trendData.length - 2] };
};

/** Antall undersøkelser med nok svar som trendene bygger på */
export const validSurveyCount = (questionTrends: TemplateQuestionTrends[]): number =>
  new Set(questionTrends.flatMap((question) => question.trendData.map((point) => point.surveyId))).size;

const isLikert = (type: string) => type === QuestionType.LIKERT_SCALE;
const isText = (type: string) => type === QuestionType.SHORT_TEXT || type === QuestionType.LONG_TEXT;

/** Grupperer spørsmålene per kategori i malens rekkefølge, spørsmål uten kategori til slutt */
export const groupResults = (questionTrends: TemplateQuestionTrends[]): CategoryGroup[] => {
  const groups = new Map<string | null, TemplateQuestionTrends[]>();
  questionTrends.forEach((question) => {
    const category = question.category || null;
    groups.set(category, [...(groups.get(category) ?? []), question]);
  });

  const ordered = [...groups.entries()].sort(([a], [b]) => Number(a === null) - Number(b === null));

  return ordered.map(([category, questions]) => {
    const responseCount = Math.max(0, ...questions.map((question) => latestPair(question)?.current.responseCount ?? 0));
    return {
      category,
      questionCount: questions.length,
      responseCount,
      questions,
      likert: questions.filter((question) => isLikert(question.questionType)),
    };
  });
};

/** Hvor mange som svarte hver verdi på skalaen, fra laveste til høyeste */
export const scaleDistribution = (
  point: TemplateQuestionTrendDataPoint,
  scaleMin: number,
  scaleMax: number,
): { value: number; count: number }[] =>
  Array.from({ length: Math.max(0, scaleMax - scaleMin + 1) }, (_, index) => {
    const value = scaleMin + index;
    return { value, count: point.optionDistribution?.[String(value)] ?? 0 };
  });

/** Hvor langt ut på skalaen et snitt ligger, fra 0 til 1 */
export const scalePosition = (value: number, scaleMin: number, scaleMax: number): number => {
  if (scaleMax <= scaleMin) return 0;
  return Math.min(1, Math.max(0, (value - scaleMin) / (scaleMax - scaleMin)));
};

export const npsCategory = (score: number): NpsCategory => {
  if (score < 0) return 'poor';
  if (score < 30) return 'good';
  if (score < 50) return 'veryGood';
  return 'excellent';
};

/**
 * Radene for valgspørsmål. Andelen regnes av antall som svarte,
 * så ved flere alternativer kan summen bli over 100 %.
 */
export const choiceRows = (
  question: TemplateQuestionTrends,
  point: TemplateQuestionTrendDataPoint,
  yesNoLabels: { yes: string; no: string },
): ChoiceRow[] => {
  const distribution = point.optionDistribution ?? {};
  const responses = point.responseCount || 0;
  const labelFor = (key: string) => {
    if (question.questionType === QuestionType.YES_NO) {
      return key === '1' ? yesNoLabels.yes : key === '0' ? yesNoLabels.no : key;
    }
    return point.options?.find((option) => String(option.id) === key)?.optionText ?? key;
  };

  const keys =
    question.questionType === QuestionType.YES_NO
      ? ['1', '0']
      : point.options?.length
        ? [...point.options].sort((a, b) => a.orderIndex - b.orderIndex).map((option) => String(option.id))
        : Object.keys(distribution);

  const rows = keys.map((key) => {
    const count = distribution[key] ?? 0;
    return { key, label: labelFor(key), count, percent: responses ? Math.round((count / responses) * 100) : 0 };
  });
  const top = Math.max(0, ...rows.map((row) => row.count));

  return rows.map((row) => ({ ...row, isTop: top > 0 && row.count === top }));
};

const locale = (language: string) => (language === 'en' ? 'en-GB' : 'nb-NO');

export const formatScore = (value: number, language: string, fractionDigits = 2) =>
  value.toLocaleString(locale(language), {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });

/** Endring med fortegn og ekte minustegn, for eksempel +0,34 eller −0,33 */
export const formatDelta = (value: number, language: string, fractionDigits = 2) => {
  const formatted = Math.abs(value).toLocaleString(locale(language), {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });
  if (Number(formatted.replace(',', '.')) === 0) return formatted;
  return `${value > 0 ? '+' : '−'}${formatted}`;
};

export const formatDate = (date: string, language: string, month: 'short' | 'long' = 'short') =>
  new Date(date).toLocaleDateString(locale(language), { day: 'numeric', month, year: 'numeric' });

/**
 * Resultatene fra én undersøkelse på samme form som trendene, med ett punkt per spørsmål,
 * så begge visningene bruker de samme kortene
 */
export const fromSurveyResults = (results: CustomSurveyResults): TemplateQuestionTrends[] =>
  results.questionStatistics.map((statistics) => ({
    templateQuestionId: statistics.questionId,
    questionText: statistics.questionText,
    questionType: statistics.questionType,
    category: statistics.category,
    scaleMin: statistics.scaleMin,
    scaleMax: statistics.scaleMax,
    scaleMinLabel: statistics.scaleMinLabel,
    scaleMaxLabel: statistics.scaleMaxLabel,
    chartType: statistics.chartType,
    trendData:
      statistics.responseCount > 0
        ? [
            {
              surveyId: results.id,
              surveyTitle: results.title,
              surveyActivatedAt: results.activatedAt ?? '',
              averageScore: statistics.averageScore,
              medianScore: statistics.medianScore,
              standardDeviation: statistics.standardDeviation,
              optionDistribution: statistics.optionDistribution,
              options: statistics.options,
              npsDistribution: statistics.npsDistribution,
              responseCount: isText(statistics.questionType)
                ? (statistics.textResponseCount ?? statistics.responseCount)
                : statistics.responseCount,
            },
          ]
        : [],
  }));
